/**
 * FiasAIInput - Chat input for the Fias AI panel.
 *
 * Structural readiness component. Will be used within FiasAIPanel for arche integration.
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import { Send, Square } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { loadPromptHistory, recordPrompt } from './lib/promptHistory';

// In-progress composer drafts, keyed so each Fias AI surface keeps its own text.
// Module-scoped so a draft survives the input unmounting/remounting — leaving the
// chat view for settings/history, minimizing, or closing and reopening the Fias
// AI window all tear down this component. Clears on a full page reload, matching
// the in-window message list (mirrors the chat composer's composerDrafts).
const composerDrafts = new Map<string, string>();

interface FiasAIInputProps {
  onSubmit: (message: string) => void;
  disabled?: boolean;
  placeholder?: string;
  /** When true, focus the textarea on mount so the user can type immediately. */
  autoFocus?: boolean;
  /**
   * When set, the in-progress draft is preserved under this key across the
   * component unmounting/remounting (tab-switch, minimize, close/reopen) — not a
   * full page reload. Omit for ephemeral inputs that shouldn't retain text.
   */
  draftKey?: string;
  /**
   * When set, a Stop button replaces Send while a turn is in flight —
   * cooperative cancel: the server finishes the current step and stops.
   */
  onStop?: () => void;
  /**
   * When set, Up/Down recall prompts already sent from this composer surface,
   * the way a shell recalls commands, and every sent prompt is recorded under
   * this key. Omit to leave the arrow keys entirely to the browser — which is
   * what every surface that doesn't opt in still gets.
   *
   * Distinct from `draftKey` on purpose: a draft is one in-flight string with
   * tab lifetime, history is a bounded list with device lifetime and a
   * different backend. See lib/promptHistory.ts.
   */
  historyKey?: string;
  /**
   * false ⇒ history lives only in memory for the life of the tab. Guest
   * sessions pass false: their user id is never revisited after the account is
   * purged, so persisting under it would strand text in a shared browser.
   */
  historyPersist?: boolean;
}

export function FiasAIInput({
  onSubmit,
  disabled = false,
  placeholder = 'Ask Fias AI...',
  autoFocus = false,
  draftKey,
  onStop,
  historyKey,
  historyPersist = true,
}: FiasAIInputProps) {
  const [value, setValue] = useState(() => (draftKey ? (composerDrafts.get(draftKey) ?? '') : ''));
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  // Where the user is in their prompt history, or null when they aren't browsing
  // it. A ref, not state: nothing renders from it, and reading it inside the key
  // handler must see the value the previous keystroke wrote. `stash` is the text
  // that was in the composer before browsing started, restored on the way out.
  const navRef = useRef<{ entries: string[]; index: number; stash: string } | null>(null);
  // Set when a recall needs the caret parked at the end once the new text renders.
  const caretToEndRef = useRef(false);

  // Update the value and mirror it into the module-scoped draft store so an
  // in-progress message outlives this component instance (see composerDrafts).
  const updateValue = useCallback(
    (next: string) => {
      setValue(next);
      if (!draftKey) return;
      if (next) composerDrafts.set(draftKey, next);
      else composerDrafts.delete(draftKey);
    },
    [draftKey],
  );

  const adjustHeight = useCallback(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${Math.min(textarea.scrollHeight, 120)}px`;
    }
  }, []);

  const moveCaretToEnd = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    const end = el.value.length;
    el.setSelectionRange(end, end);
  }, []);

  useEffect(() => {
    adjustHeight();
    // A history recall asked for the caret at the end; the new text is on the
    // DOM node by now, so this is the first moment that can honor it.
    if (!caretToEndRef.current) return;
    caretToEndRef.current = false;
    moveCaretToEnd();
  }, [value, adjustHeight, moveCaretToEnd]);

  // Keep the composer focused so a conversation can be carried on by typing
  // alone — no clicking back into the box. This fires:
  //  - on mount (panel opens; the popup unmounts on close, so this re-runs)
  //  - whenever the input transitions back to enabled, i.e. after the assistant
  //    finishes a turn or pauses to ask a question (it's disabled while
  //    "thinking", which blurs the textarea).
  // The caret is placed at the end so a restored draft is ready to continue.
  useEffect(() => {
    if (!autoFocus || disabled) return;
    const el = textareaRef.current;
    if (!el) return;
    el.focus();
    const end = el.value.length;
    el.setSelectionRange(end, end);
  }, [autoFocus, disabled]);

  // Drop any browsing position when the surface changes — including Temporary
  // chat switching the key off and back on, which would otherwise resume against
  // a stale snapshot and a stale stash.
  useEffect(() => {
    navRef.current = null;
  }, [historyKey]);

  // Replace the composer contents from history and park the caret at the end,
  // the way a shell leaves a recalled command ready to run or edit. The caret
  // move is deferred to the post-commit effect below, because the new text isn't
  // on the DOM node yet — unless the text is unchanged, in which case there is
  // no commit to wait for.
  const applyRecalled = useCallback(
    (next: string) => {
      if (next === value) {
        moveCaretToEnd();
        return;
      }
      caretToEndRef.current = true;
      updateValue(next);
    },
    [value, updateValue, moveCaretToEnd],
  );

  /**
   * Step through history. `delta` is +1 for older (ArrowUp) and -1 for newer
   * (ArrowDown). Returns false when there was nothing to do, so the caller can
   * leave the keystroke to the browser.
   *
   * The snapshot is read on the way IN and dropped on the way out, so no
   * storage is touched on mount and a clear from Options takes effect the next
   * time the user browses.
   */
  const navigateHistory = useCallback(
    (delta: 1 | -1): boolean => {
      if (!historyKey) return false;
      const browsing = navRef.current;

      if (!browsing) {
        // ArrowDown with nothing recalled is ordinary caret movement.
        if (delta === -1) return false;
        const entries = loadPromptHistory(historyKey, { persist: historyPersist });
        if (entries.length === 0) return false;
        navRef.current = { entries, index: 0, stash: value };
        applyRecalled(entries[0]);
        return true;
      }

      const nextIndex = browsing.index + delta;
      if (nextIndex >= browsing.entries.length) return true; // at the oldest — hold, never wrap
      if (nextIndex < 0) {
        // Stepped back past the newest: restore whatever was being typed before.
        const { stash } = browsing;
        navRef.current = null;
        applyRecalled(stash);
        return true;
      }
      navRef.current = { ...browsing, index: nextIndex };
      applyRecalled(browsing.entries[nextIndex]);
      return true;
    },
    [historyKey, historyPersist, value, applyRecalled],
  );

  const handleSubmit = () => {
    const trimmed = value.trim();
    if (!trimmed || disabled) return;
    if (historyKey) recordPrompt(historyKey, trimmed, { persist: historyPersist });
    navRef.current = null;
    onSubmit(trimmed);
    updateValue('');
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Never steal a key mid-IME composition — an Enter or arrow there belongs to
    // the input method picking a candidate, not to us.
    if (e.nativeEvent.isComposing) return;

    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
      return;
    }

    if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return;
    // Modified arrows are OS/browser text navigation; a selection means the user
    // is extending it. Neither is a history request.
    if (e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) return;
    const el = textareaRef.current;
    if (!el || el.selectionStart !== el.selectionEnd) return;

    // Two modes, because editing and browsing want opposite things from the
    // arrow keys.
    //
    // ENTERING history takes a caret at the very start (up) or very end (down).
    // That boundary is a text offset rather than a guess at which line the caret
    // is on, so it stays correct when a long prompt SOFT-WRAPS across rows: the
    // browser walks the caret up through the wrapped rows first, exactly as it
    // would in any other textarea, and history takes over only when there is
    // nowhere left to go. An empty composer is already at both boundaries, so
    // the common case is still one keypress.
    //
    // Once ON an unedited recalled entry the user is plainly browsing, not
    // editing, so the arrows step through history directly — otherwise every
    // step would cost a second keypress to walk the caret back to the boundary.
    // Typing anything drops them back into editing (the value stops matching the
    // recalled entry) and the boundary rule applies again.
    const browsing = navRef.current;
    const onUneditedRecall = browsing !== null && el.value === browsing.entries[browsing.index];
    if (!onUneditedRecall) {
      const caret = el.selectionStart;
      if (e.key === 'ArrowUp' ? caret !== 0 : caret !== el.value.length) return;
    }

    if (!navigateHistory(e.key === 'ArrowUp' ? 1 : -1)) return;
    e.preventDefault();
    // Keep the keystroke out of any arche-level hotkey registry underneath the
    // window (image-editor and photo-editor both bind ArrowUp to nudge).
    e.stopPropagation();
  };

  return (
    <div className="border-t border-border p-2">
      <div className="relative">
        <Textarea
          ref={textareaRef}
          value={value}
          onChange={(e) => updateValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          className="min-h-[36px] max-h-[120px] pr-10 resize-none rounded-lg text-sm bg-muted/50"
          rows={1}
          style={{ height: 'auto' }}
        />
        {onStop ? (
          <Button
            type="button"
            size="icon"
            variant="destructive"
            onClick={onStop}
            title="Stop"
            aria-label="Stop the assistant"
            className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 rounded-md"
            data-testid="fias-ai-stop"
          >
            <Square className="h-3 w-3 fill-current" />
          </Button>
        ) : (
          <Button
            type="button"
            size="icon"
            disabled={!value.trim() || disabled}
            onClick={handleSubmit}
            className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 rounded-md"
          >
            <Send className="h-3 w-3" />
          </Button>
        )}
      </div>
    </div>
  );
}
